


































import {
  Component,
  Inject,
  Prop,
  Vue
} from 'vue-property-decorator';
import { ConfigService } from '@/services/ConfigService';
import { ApiService } from '@/services/ApiService';
import { UnitDefinition } from '@/services/ProductDeserializer';
import { OrderTemplateDto, OrderTemplateItemDto } from '@eurogast/hub-client-typescript';
import MatomoContext from '@/matomo/domain/MatomoContext';
import MatomoEvents from '@/matomo/events/MatomoEvents';
import OrderTemplateApiService from '@/services/template/OrderTemplateApiService';
import OrderTemplateSelectItem from '@/domain/templates/OrderTemplateSelectItem';
import { OrderTemplateEvents } from '@/domain/templates/OrderTemplateEvents';

@Component
export default class OrderTemplateSelection extends Vue {

  get containsAtLeastOne() {
    return this.selectItems.find((el) => el.containsSku) != null;
  }

  public isLoggedIn: boolean = ConfigService.isLoggedIn();
  public showOverlay = false;

  public selectItems: OrderTemplateSelectItem[] = [];

  @Prop({ required: false, default: () => [] })
  public orderTemplates: OrderTemplateDto[];
  @Prop({ required: true })
  public productSku: string;
  @Prop({ required: true })
  public baseAmount: number;
  @Prop({ required: false, default: true })
  public simpleView: boolean;
  @Prop({ required: false, default: 'auto' })
  public zIndex: string;
  @Prop({ required: false, default: null })
  public currentUnit: UnitDefinition;

  @Inject({ default: MatomoContext.default() })
  public matomoContext: MatomoContext;

  public defaultUnit?: UnitDefinition = {
    name: '',
    abbreviation: '',
    ratio: 1.0,
    isDefault: false,
    isStorageUnit: false
  };

  private newOrderTemplateName = null;

  private orderTemplatesApiService = OrderTemplateApiService.singleton();

  public handleOutsideClick() {
    this.hideSelection();
  }

  public mounted() {
    this.$events.on(OrderTemplateEvents.templateCreated, (eventData) => this.handleOrderTemplatedAdded(eventData));
    this.selectItems = this.orderTemplates.map((el) => new OrderTemplateSelectItem(el, this.productSku));
    this.sortSelectionItemsByName();
  }

  private handleOrderTemplatedAdded(newTemplate: OrderTemplateDto) {
    /*
        Once we have a vue-component for the product-list (i.e search/browse/essences),
        we should get rid of this event handling and just inject into a store in the
        highlevel component
     */
    this.selectItems.push(new OrderTemplateSelectItem(newTemplate, this.productSku));
    this.sortSelectionItemsByName();
  }

  public async addNewOrderTemplate() {
    if (this.newOrderTemplateName != null && this.newOrderTemplateName !== '') {
      const orderTemplate = this.buildNewOrderTemplate();
      try {
        let templateDto = await this.orderTemplatesApiService.createOrderTemplate(orderTemplate);
        if (templateDto != null) {
          templateDto = await this.orderTemplatesApiService.addItemToTemplate(
              templateDto.id,
              this.buildOrderTemplateItem()
          );
          this.sortSelectionItemsByName();
          this.newOrderTemplateName = null;
          this.showOverlay = false;
          Vue.prototype.$events.$emit(OrderTemplateEvents.templateCreated, templateDto);
          MatomoEvents.forOrderTemplates.addTemplate.publishWithContext(this.matomoContext);
        }
      } catch (e) {
        await ApiService.defaultErrorHandler(e);
      }
    }
  }

  public swapOrderTemplateItem(selectItem: OrderTemplateSelectItem) {
    if (selectItem.containsSku) {
      this.removeProductFromOrderTemplate(selectItem);
    } else {
      this.addProductToOrderTemplate(selectItem);
    }
  }

  public async addProductToOrderTemplate(selectItem: OrderTemplateSelectItem) {
    try {
      await this.orderTemplatesApiService.addItemToTemplate(selectItem.templateId, this.buildOrderTemplateItem());
      selectItem.containsSku = true;
      MatomoEvents.forOrderTemplates.addProduct.publishWithContext(this.matomoContext);
    } catch (e) {
      await ApiService.defaultErrorHandler(e);
    }
  }

  public async removeProductFromOrderTemplate(selectItem: OrderTemplateSelectItem) {
    try {
      await this.orderTemplatesApiService.removeItemFromTemplate(selectItem.templateId, this.buildOrderTemplateItem());
      selectItem.containsSku = false;
      const eventData = { orderTemplateId: selectItem.templateId, productSku: this.productSku };
      Vue.prototype.$events.$emit(OrderTemplateEvents.productRemoved, eventData);
      MatomoEvents.forOrderTemplates.removeProduct.publishWithContext(this.matomoContext);
    } catch (e) {
      await ApiService.defaultErrorHandler(e);
    }
  }

  public toggleShowSelection() {
    if (this.showOverlay) {
      this.hideSelection();
    } else {
      this.showSelection();
    }
  }

  public hideSelection() {
    this.showOverlay = false;
    window.removeEventListener('click', this.handleOutsideClick);
  }

  public showSelection() {
    this.showOverlay = true;
    window.addEventListener('click', this.handleOutsideClick);
  }

  get currentUnitOrDefault() {
    if (this.currentUnit != null) {
      return this.currentUnit;
    }
    return this.defaultUnit;
  }

  private sortSelectionItemsByName() {
    this.selectItems.sort((item1, item2) => item1.templateName.localeCompare(item2.templateName));
  }

  private buildNewOrderTemplate(): OrderTemplateDto {
    return {
      id: null,
      name: this.newOrderTemplateName,
      comment: '',
      customerId: ConfigService.currentCustomerId,
      sharedWith: []
    };
  }

  private buildOrderTemplateItem(): OrderTemplateItemDto {
    return {
      productSku: this.productSku,
      amount: this.baseAmount,
      unit: this.currentUnitOrDefault.abbreviation
    };
  }
}
